<template>
  <div v-if="isLoadingFetch" class="flex items-center gap-1.5 text-zinc-500">
    <Icon class="animate-spin text-xl" name="progress_activity" />
    <span>{{ __('Loading...') }}</span>
  </div>

  <div v-else class="space-y-6 md:space-y-8">
    <div class="flex items-center gap-2 md:gap-4">
      <Icon class="!text-3xl text-primary" name="badge" type="filled" />
      <h1 class="text-2xl font-medium text-zinc-800 md:text-4xl">
        {{ __('Let’s get to know you better') }}
      </h1>
    </div>

    <form class="space-y-8" @submit.prevent="handleSubmit">
      <div class="grid gap-4 md:grid-cols-2">
        <InputDatePicker
          v-if="fields.birth_date.visible"
          id="birth_date"
          v-model:value="fields.birth_date.value"
          v-model:errors="fields.birth_date.errors"
          :label="__('Enter your date of birth')"
          :placeholder="__('Birth date')"
          :required="fields.birth_date.mandatory"
          :max="new Date()"
        />

        <InputText
          v-if="fields.educational_institution.visible"
          id="educational_institution"
          v-model:value="fields.educational_institution.value"
          v-model:errors="fields.educational_institution.errors"
          :label="__('Enter your school or university')"
          :placeholder="__('Educational institution where you are enrolled')"
          :required="fields.educational_institution.mandatory"
        >
          <template #icon>
            <Icon name="account_balance" class="text-lg" />
          </template>
        </InputText>
      </div>

      <InputLocation
        v-if="fields.location.visible"
        v-model:value="fields.location.value"
        v-model:errors="fields.location.errors"
        :label-country="__('Country of residence')"
        :label-city="__('City of residence')"
        :placeholder-country="__('The country you live in')"
        :placeholder-city="__('The city you live in')"
        :options="fields.location.options"
        :required="fields.location.mandatory"
        :default-country="fields.location.default_country"
      />

      <InputButtonGroup
        v-if="fields.reasons_for_joining.visible"
        id="reasons_for_joining"
        v-model:value="fields.reasons_for_joining.value"
        v-model:errors="fields.reasons_for_joining.errors"
        :label="__('Why did you join?')"
        :description="__('Pick the options from the list below.')"
        :required="fields.reasons_for_joining.mandatory"
        :options="fields.reasons_for_joining.options"
      />

      <div class="flex items-center justify-between">
        <Button
          class="flex items-center gap-1.5"
          variant="ghost"
          @click="handleBackButtonClick"
        >
          <Icon class="text-lg" name="arrow_back_ios_new" />
          <span>{{ __('Go Back') }}</span>
        </Button>

        <Button
          class="ml-auto flex items-center gap-1.5"
          type="submit"
          :disabled="isLoadingUpdate"
        >
          <Icon
            v-if="isLoadingUpdate"
            class="animate-spin text-lg"
            name="progress_activity"
          />

          <span>{{ __('Next') }}</span>
        </Button>
      </div>
    </form>
  </div>
</template>

<script setup>
  import { onMounted, ref } from 'vue';
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import { __ } from '@/helpers';
  import { Button } from '@/components/block/button';

  import { InputButtonGroup } from '@/components/form/input-button-group';
  import { InputDatePicker } from '@/components/form/input-date-picker';
  import { InputLocation } from '@/components/form/input-location';
  import { InputText } from '@/components/form/input-text';
  import Icon from '@/components/global/Icon.vue';

  const emits = defineEmits(['back', 'next', 'update:conditions']);

  const toast = useToast();

  const fields = ref({
    birth_date: {
      visible: false,
      mandatory: false,
      value: null,
      errors: []
    },
    educational_institution: {
      visible: false,
      mandatory: false,
      value: null,
      errors: []
    },
    location: {
      visible: false,
      mandatory: false,
      value: null,
      errors: [],
      options: []
    },
    reasons_for_joining: {
      visible: false,
      mandatory: false,
      value: [],
      errors: [],
      options: []
    }
  });
  const isLoadingFetch = ref(false);
  const isLoadingUpdate = ref(false);

  function isValid(value) {
    if (!value) {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'object') {
      for (const prop in value) {
        if (value[prop] === null) {
          return false;
        }
      }
      return true;
    }

    return true;
  }

  async function fetchData() {
    isLoadingFetch.value = true;

    try {
      const response = await axios.get(
        route('onboarding.step', { slug: 'details' })
      );

      Object.entries(response.data).forEach(([key, value]) => {
        if (fields.value[key]) {
          fields.value[key] = { ...fields.value[key], ...value };
        }
      });
    } catch {
      toast.error(
        __('An unexpected error has occured. Please try again later.')
      );
    }

    isLoadingFetch.value = false;
  }

  function handleBackButtonClick() {
    emits('back');
  }

  async function handleSubmit() {
    const requiredFieldKeys = Object.keys(fields.value).filter(
      (key) => fields.value[key].visible && fields.value[key].mandatory
    );
    let firstInvalidField;

    requiredFieldKeys.forEach((key) => {
      if (!isValid(fields.value[key].value)) {
        fields.value[key].errors = [__('This field is required')];

        const element = document.getElementById(key);

        if (element && !firstInvalidField) {
          firstInvalidField = element;
        }
      }
    });

    if (
      Object.values(fields.value).some(
        (field) => field.errors && field.errors.length
      )
    ) {
      if (firstInvalidField) {
        firstInvalidField.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
      return;
    }

    const payload = Object.keys(fields.value).reduce((acc, key) => {
      if (fields.value[key].visible) {
        acc[key] = fields.value[key].value;
      }
      return acc;
    }, {});

    isLoadingUpdate.value = true;

    try {
      const response = await axios.post(
        route('onboarding.step', { slug: 'details' }),
        payload
      );

      if (response.data.conditions) {
        emits('update:conditions', response.data.conditions);
      }

      emits('next');
    } catch (error) {
      if (error?.response?.data?.errors) {
        Object.keys(error.response.data.errors).forEach((key) => {
          if (fields.value[key]) {
            fields.value[key].errors = error.response.data.errors[key];
          }
        });
      } else {
        toast.error(
          __('An unexpected error has occured. Please try again later.')
        );
      }
    }

    isLoadingUpdate.value = false;
  }

  onMounted(async () => {
    await fetchData();
  });
</script>
